import { AuthAdapter, AuthUnauthorizedError } from '@uofu-uss-gis/ui-react';
import { env } from '../config/env';
import { loadOidcService } from '../services/oidcLoader';
import { getWSO2Config, getCurrentEnvironment } from '../config/wso2Config';
import { getOidcUserData, isTokenDataExpired } from '../services/oidcStorage';

const isLocalMode = env.localMode;

const localUnid = env.localUnid;

/**
 * Interface representing the class schedule data structure returned from the backend.
 * This matches the Kotlin ClassSchedule entity's JSON serialization format.
 */
export interface BackendClassSchedule {
  emplId: string | null;
  unid: string;
  name: string | null;
  term: string | null;
  description: string | null;
  campus: string | null;
  courseTitle: string | null;
  buildingCode: string | null;
  courseLocation: string | null;
  days: string | null;
  startEndTime: string | null;
  startDate: string | null;
  endDate: string | null;
  instructorList: string | null;
}

/**
 * Interface representing the transformed class schedule data used by the frontend.
 * Uses legacy attribute names for compatibility with existing UI components.
 */
export interface ClassInfo {
  attributes: {
    BLDG_CD: string;
    UU_COURSE_TITLE: string;
    UU_CRSE_DAYS: string;
    UU_CRS_STRT_END_TM: string;
    UU_CRSE_LOC: string;
    UU_INSTR_LIST: string;
    START_DT: number;
    END_DT: number;
    NAME: string;
    DESCR: string;
    OBJECTID: number;
  };
}

/**
 * Interface representing an authenticated CAS user.
 */
export interface CasUser {
  unid: string;
  name?: string;
  [key: string]: any;
}

/**
 * Cache for class schedules data to prevent duplicate API calls.
 * Cleared after each authentication cycle to ensure fresh data.
 */
let classSchedulesCache: BackendClassSchedule[] | null = null;

/**
 * Clears the class schedules cache.
 * Called after both fetchUser and fetchPayload complete.
 */
function clearClassSchedulesCache(): void {
  classSchedulesCache = null;
}

/**
 * Extracts user information from the OIDC session storage.
 * Returns the user's UNID and name from the stored profile data.
 */
function getUserFromOIDC(): { unid: string; name: string } | null {
  try {
    // Deterministic selection: current client_id scoped, unexpired, freshest token.
    const userData = getOidcUserData();
    if (!userData) {
      return null;
    }

    // Extract user info from the profile object
    // WSO2 uses URL-based claim names like "http://wso2.org/claims/displayName"
    const profile = (userData.profile ?? {}) as Record<string, any>;
    const unid = profile.sub || profile.preferred_username || profile.uNID || 'unknown';
    
    // Try multiple name fields in order of preference
    const name = profile['http://wso2.org/claims/displayName'] 
      || profile.displayName 
      || profile.name 
      || profile['http://wso2.org/claims/givenname']
      || profile.given_name 
      || 'User';
    
    return { unid, name };
  } catch (error) {
    console.error('Error extracting user from OIDC:', error);
    return null;
  }
}

/**
 * Wrapper that sends a standard Bearer token in Authorization.
 * WSO2 is responsible for validating the frontend token and forwarding backend auth.
 */
async function callApiWithAuth(path: string, options: any = {}): Promise<Response> {
  // Get token from sessionStorage (where USS OIDC stores it) using deterministic
  // selection - avoids picking a stale/wrong-environment token when multiple
  // oidc.user:* entries exist (reused browser contexts, load runners).
  const userData = getOidcUserData();
  if (!userData) {
    throw new Error('No authentication token found');
  }

  const token = userData.access_token;
  
  if (!token) {
    throw new Error('No access token available');
  }
  
  // Build the full URL based on environment
  const config = getWSO2Config();
  const env = getCurrentEnvironment();
  
  // Gateway URL varies by environment:
  // - dev/test: gateway.uapi.dev.utah.edu:8243
  // - production: gateway.uapi.utah.edu:8243
  const gatewayUrl = env === 'production' 
    ? 'https://gateway.uapi.utah.edu:8243'
    : 'https://gateway.uapi.dev.utah.edu:8243';
  
  const baseUrl = `${gatewayUrl}${config.apiPath}`;
  
  // Make the request with Authorization only
  return fetch(`${baseUrl}/${path}`, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    },
  });
}

/**
 * Single-flight guard for token renewal. Under concurrent load, multiple in-flight
 * requests can hit 401 simultaneously; without this guard each would trigger its own
 * silent-renew, racing each other and potentially invalidating one another's tokens.
 */
let tokenRenewalPromise: Promise<boolean> | null = null;

/**
 * Attempts a silent token renewal via the lazily-loaded OIDC service.
 * Concurrent callers share a single renewal attempt.
 * @returns true when a fresh token is available, false otherwise.
 */
async function renewTokenOnce(): Promise<boolean> {
  if (!tokenRenewalPromise) {
    tokenRenewalPromise = (async () => {
      try {
        const oidcService = await loadOidcService();
        if (!oidcService?.renewToken) {
          return false;
        }
        return await oidcService.renewToken();
      } catch (error) {
        console.error('Token renewal failed:', error);
        return false;
      }
    })().finally(() => {
      tokenRenewalPromise = null;
    });
  }
  return tokenRenewalPromise;
}

/**
 * API call wrapper with request-time token recovery:
 *  1. If the stored token is already expired, renew it BEFORE making the call
 *     (avoids a guaranteed 401 round-trip).
 *  2. If the call still returns 401, renew once and retry exactly once.
 * 403 is NOT retried - it signals an authorization decision, not an expired token.
 */
async function callApiWithAuthRetry(path: string, options: any = {}): Promise<Response> {
  // Proactive renewal: don't send a token we already know is expired.
  if (isTokenDataExpired(getOidcUserData())) {
    await renewTokenOnce();
  }

  let response = await callApiWithAuth(path, options);

  if (response.status === 401) {
    const renewed = await renewTokenOnce();
    if (renewed) {
      response = await callApiWithAuth(path, options);
    }
  }

  return response;
}

/**
 * Local API request path for localhost development.
 * Requests are proxied by Vite to the backend and pass UNID through a dev-only header.
 */
async function callLocalApi(path: string, options: any = {}): Promise<Response> {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return fetch(normalizedPath, {
    ...options,
    headers: {
      ...options.headers,
      'X-Dev-Unid': localUnid,
    },
  });
}

/**
 * Authentication adapter for the My Class Map application.
 * Handles fetching user information and class schedules from the backend API.
 */
export const classMapAuthAdapter: AuthAdapter<CasUser, ClassInfo[]> = {
  /**
   * Fetches user information from the class schedules API.
   * Extracts user details from the first class schedule entry.
   * Caches the response to avoid duplicate API calls in fetchPayload.
   * @returns CasUser object with unid and name
   */
  fetchUser: async () => {
    try {
      // First, try to get user info from OIDC session storage
      const oidcUser = getUserFromOIDC();

      const response = isLocalMode
        ? await callLocalApi('api/class-schedules', { method: 'GET' })
        : await callApiWithAuthRetry('api/class-schedules', { method: 'GET' });

      // Check for authentication errors
      if (response.status === 401 || response.status === 403) {
        throw new AuthUnauthorizedError();
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('WSO2 API request failed:', response.status, errorText);
        throw new Error(`WSO2 API request failed: ${response.status}`);
      }

      const data = await response.json();
      
      // Cache the response for fetchPayload to reuse
      classSchedulesCache = data;
      
      // If no classes found, use OIDC user info or return default
      if (!data || data.length === 0) {
        if (isLocalMode) {
          return { unid: localUnid, name: 'Local User' };
        }
        return oidcUser || { unid: 'unknown', name: 'User' };
      }
      
      // Extract user info from first class schedule entry
      // Prefer class schedule data, fall back to OIDC data
      const firstClass = data[0];
      return {
        unid: firstClass.unid || (isLocalMode ? localUnid : oidcUser?.unid) || 'unknown',
        name: firstClass.name || oidcUser?.name || (isLocalMode ? 'Local User' : 'User'),
      };
    } catch (error) {
      if (error instanceof AuthUnauthorizedError) {
        throw error;
      }
      console.error('Error fetching user from WSO2:', error);
      clearClassSchedulesCache();
      throw new Error(`Failed to authenticate with WSO2: ${error}`);
    }
  },
  
  /**
   * Fetches the full list of class schedules for the authenticated user.
   * Transforms backend data format to frontend format for UI compatibility.
   * Uses cached data from fetchUser to avoid duplicate API calls.
   * The user parameter from AuthAdapter is intentionally omitted - the API
   * determines the user from the auth token.
   * @returns Array of ClassInfo objects with transformed attributes
   */
  fetchPayload: async () => {
    try {
      let data: BackendClassSchedule[];
      
      // Use cached data if available, otherwise fetch from API
      if (classSchedulesCache !== null) {
        data = classSchedulesCache;
      } else {
        const response = isLocalMode
          ? await callLocalApi('api/class-schedules', { method: 'GET' })
          : await callApiWithAuthRetry('api/class-schedules', { method: 'GET' });

        if (response.status === 401 || response.status === 403) {
          throw new AuthUnauthorizedError();
        }

        if (!response.ok) {
          const errorText = await response.text();
          console.error('WSO2 API request failed:', response.status, errorText);
          throw new Error(`WSO2 API request failed: ${response.status}`);
        }

        data = await response.json();
      }
      
      // Validate response is an array
      if (!Array.isArray(data)) {
        console.error('Unexpected response format from WSO2 API:', data);
        return [];
      }
      
      // Transform backend data format to frontend format
      // Maps camelCase backend fields to UPPERCASE_SNAKE_CASE legacy format
      const transformedData = data.map((item: BackendClassSchedule) => ({
        attributes: {
          BLDG_CD: item.buildingCode || '', // Building code (e.g., "WEB", "SMBB")
          UU_COURSE_TITLE: item.courseTitle || '', // Full course title
          UU_CRSE_DAYS: item.days || '', // Days of week (e.g., "MoWeFr")
          UU_CRS_STRT_END_TM: item.startEndTime || '', // Class time (e.g., "09:00 AM - 10:00 AM")
          UU_CRSE_LOC: item.courseLocation || '', // Room location (e.g., "WEB 1250")
          UU_INSTR_LIST: item.instructorList || '', // Instructor names
          START_DT: item.startDate ? new Date(item.startDate).getTime() : 0, // Convert ISO date to timestamp
          END_DT: item.endDate ? new Date(item.endDate).getTime() : 0, // Convert ISO date to timestamp
          NAME: item.name || '', // Student name
          DESCR: item.description || '', // Semester description
          OBJECTID: item.emplId ? parseInt(item.emplId, 10) : 0, // Employee/Student ID as number
        },
      }));
      
      // Clear cache after successful transformation
      clearClassSchedulesCache();
      
      return transformedData;
    } catch (error) {
      if (error instanceof AuthUnauthorizedError) {
        throw error;
      }
      console.error('Error fetching class schedules from WSO2:', error);
      clearClassSchedulesCache();
      throw new Error(`Failed to fetch class schedules: ${error}`);
    }
  },
  
  /**
   * Handler called when authentication fails.
   * Logs out the user and displays an alert.
   */
  onUnauthorized: () => {
    if (isLocalMode) {
      alert('Local mode request was unauthorized. Verify backend local profile is active.');
      return;
    }
    void loadOidcService().then(oidcService => oidcService?.logout());
    alert('Authentication failed. Please log in again.');
  }
};
